// ─────────────────────────────────────────────────────────────────────────────
// A THEME, SHOWN RATHER THAN NAMED.
//
// The picker used to show each preset as a label over a 6px bar of its brand colour. A
// preset is not a colour: it is a canvas, an ink, a rule weight, a corner radius and a
// pair of typefaces, and the difference between "Survey exhibit" and "Clinical
// workstation" is mostly in the last three. A hairline black rule with zero radius and a
// serif reads as a paper form; the same brand hue with 10px corners and a narrow sans
// reads as an EHR. One bar of colour cannot tell you which you are about to choose.
//
// So this draws a miniature of the thing: the ground, a heading in the preset's own
// display face, two rows of text, a rule at its real weight and colour, and a control in
// the brand. Every value comes from the preset's own tokens, so a thumbnail cannot drift
// from what saving would actually do.
//
// ── THE ONE HONEST LIMIT ────────────────────────────────────────────────────
//
// Webfonts are injected for the ACTIVE theme only, so an unselected preset's face is
// usually not loaded and the browser falls back down its stack — Source Serif 4 becomes
// Georgia, Archivo Narrow becomes Arial Narrow. The thumbnail therefore shows the right
// CLASS of face (serif against narrow sans) and not always the exact one. Hovering a
// preset applies it for real, which is the preview that is exact; this is the one that
// lets you decide what to hover.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";

/**
 * @param {object} props
 * @param {object} props.tokens  the preset's own token map
 * @param {number} [props.h]     height in px
 */
export default function ThemeThumb({ tokens = {}, h = 56 }) {
  const t = (k, fallback) => tokens[k] || fallback;
  const canvas = t("canvas", "#ffffff");
  const ink = t("ink", "#111111");
  const muted = t("muted", "#777777");
  const line = t("line", "#dddddd");
  const brand = t("brand", "#333333");
  const brandSoft = t("brand-soft", "#eeeeee");
  const serif = t("serif", "Georgia, serif");
  const sans = t("sans", "system-ui, sans-serif");
  const rMd = t("r-md", "6px");
  const rPill = t("r-pill", "999px");

  return (
    <span
      aria-hidden="true"
      style={{
        display: "block",
        height: h,
        borderRadius: rMd,
        background: canvas,
        border: `1px solid ${line}`,
        overflow: "hidden",
        padding: "5px 6px",
        position: "relative",
      }}
    >
      {/* The heading, in the preset's display face. This is where a serif lineage and a
          narrow sans separate at a glance. */}
      <span style={{ display: "block", fontFamily: serif, fontSize: 9, fontWeight: 700, color: ink, lineHeight: 1.1 }}>
        Facility
      </span>

      {/* A rule at its real weight and colour. Survey exhibit's is a black hairline;
          Editorial's is a warm grey. That contrast is most of the identity. */}
      <span style={{ display: "block", height: 1, background: line, margin: "4px 0 4px" }} />

      {/* Two rows of body text, as bars, in ink and muted. */}
      <span style={{ display: "block", height: 2.5, width: "72%", borderRadius: 1, background: ink, opacity: 0.75 }} />
      <span style={{ display: "block", height: 2.5, width: "52%", borderRadius: 1, background: muted, opacity: 0.7, marginTop: 3 }} />

      {/* A control in the brand, at the preset's pill radius — zero on the paper-form
          presets, fully round on the others. */}
      <span
        style={{
          position: "absolute", right: 6, bottom: 5,
          display: "inline-block", padding: "1px 6px",
          borderRadius: rPill, background: brandSoft, border: `1px solid ${brand}`,
          fontFamily: sans, fontSize: 7, fontWeight: 700, color: brand, lineHeight: 1.4,
        }}
      >
        Aa
      </span>
    </span>
  );
}
